<template>
  <div class="partnership-form">
    <Fader ref="saveIndicator" class="save-indicator">
      <p class="text-success">Saved</p>
    </Fader>

    <el-form ref="formRef" label-width="130px" :model="form" :rules="formRules">
      <el-form-item label="Name" prop="name">
        <el-input v-model="form.name" placeholder="Name" />
      </el-form-item>
      <el-form-item label="Slug" prop="slug">
        <el-input v-model="form.slug" placeholder="slug" />
      </el-form-item>
      <el-form-item label="Logo">
        <div class="image-wrapper">
          <el-image
            v-if="form.logo_url"
            :src="form.logo_url | cld({ h: 120, c: 'limit' })"
            class="logo-image"
          />
          <Dropzone
            :title="form.logo_url ? 'Change logo' : 'Upload logo'"
            :plain-title="!!form.logo_url"
            @upload-done="onImageUploaded"
          />
        </div>
      </el-form-item>
      <el-form-item v-if="false" label="Is published">
        <el-switch v-model="form.is_published" />
      </el-form-item>

      <el-form-item label="Partnership">
        <el-radio v-model="form.is_startup" :label="false"> Partner </el-radio>
        <el-radio v-model="form.is_startup" :label="true">
          Startup Launchpad
        </el-radio>
      </el-form-item>

      <el-form-item label="Is published">
        <el-radio v-model="form.is_published" :label="true">
          Published
        </el-radio>
        <el-radio v-model="form.is_published" :label="false"> Draft </el-radio>
      </el-form-item>

      <el-form-item label="Visibility">
        <el-radio v-model="form.approved_startups_only" :label="false">
          All startups
        </el-radio>
        <el-radio v-model="form.approved_startups_only" :label="true">
          Approved startups
        </el-radio>
      </el-form-item>

      <el-form-item label="Website URL">
        <el-input v-model="form.website_url" placeholder="URL" />
      </el-form-item>
      <el-form-item label="Tags">
        <el-select
          v-model="form.tags"
          multiple
          filterable
          allow-create
          default-first-option
          placeholder="Select"
          class="is-fullwidth"
        >
          <el-option
            v-for="tag in allTags"
            :key="tag"
            :label="tag"
            :value="tag"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="Description">
        <TextEditor
          bottom-border
          :text="form.description"
          @blur="
            (d) => {
              form.description = d
            }
          "
        />
      </el-form-item>

      <el-form-item label="Type">
        <el-radio v-model="form.type" label="PROMO">
          Promo code / link
        </el-radio>
        <el-radio v-model="form.type" label="MAIL_INTRO">
          Intro via e-mail
        </el-radio>
        <el-radio
          v-if="value.id === AMAZON_ID"
          v-model="form.type"
          label="AWS_CUSTOM"
        >
          AWS
        </el-radio>
      </el-form-item>

      <template v-if="form.type === 'PROMO'">
        <el-form-item label="Button text">
          <el-input
            v-model="form.promo_link_url_text"
            :placeholder="DEFAULT_PROMO_LINK_URL_TEXT"
          />
        </el-form-item>

        <el-form-item label="Registration URL" prop="promo_link_url">
          <el-input
            v-model="form.promo_link_url"
            placeholder="Registration URL"
          />
        </el-form-item>
        <el-form-item label="Promo Code">
          <el-input v-model="form.promo_code" placeholder="Code" />
        </el-form-item>
      </template>

      <template v-if="form.type === 'MAIL_INTRO'">
        <el-form-item label="Button text">
          <el-input v-model="form.mail_intro_text" />
        </el-form-item>
        <el-form-item label="E-mail">
          <el-input v-model="form.email" />
        </el-form-item>
      </template>

      <template v-if="form.type === 'AWS_CUSTOM'"> </template>

      <el-form-item>
        <el-button type="primary" :loading="saveInProgress" @click="save()">
          Save
        </el-button>
        <el-button @click="cancel()"> Cancel </el-button>

        <el-button
          v-if="value.id"
          :loading="deleteInProgress"
          class="pull-right"
          type="danger"
          plain
          @click="confirmRemoval()"
        >
          Remove
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { ref, computed, watch, reactive } from '@vue/composition-api'
import TextEditor from '@/components/TextEditor'
import Fader from '@/components/Fader'
import store from 'store'
import { DEFAULT_PROMO_LINK_URL_TEXT } from 'services/marketplace-partnerships'
import Dropzone from '@/components/Dropzone'
import _ from 'lodash'

const AMAZON_ID = 'cc0ff6d7b5474b059a042a931b9585a9'

export default {
  components: {
    Dropzone,
    TextEditor,
    Fader,
  },
  props: {
    value: { type: Object, default: () => ({}) },
    partnerships: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit, root }) {
    const formRef = ref(null)
    const form = ref(initForm())
    const errors = reactive({})
    if (props.value) {
      form.value = { ...props.value }
    }

    const formRules = {
      name: [
        {
          validator(rule, value, callback) {
            if (!value && form.value.name === '') {
              callback(new Error('This field is required'))
            }
            const otherPartnerships = props.partnerships.filter(
              (p) => p.id !== props.value.id,
            )
            const otherWithSameSName = otherPartnerships.filter(
              (p) => p.name === value,
            )[0]
            if (otherWithSameSName) {
              console.warn(
                `Another partnership has same name (id: ${otherWithSameSName.id})`,
              )
              callback(new Error('Another partnership has same name'))
            }
            callback()
          },
        },
      ],
      slug: [
        {
          validator(rule, value, callback) {
            if (!value) {
              callback(new Error('This field is required'))
            }
            const otherPartnerships = props.partnerships.filter(
              (p) => p.id !== props.value.id,
            )
            const otherWithSameSlug = otherPartnerships.filter(
              (p) => p.slug === value,
            )[0]
            if (otherWithSameSlug) {
              console.warn(
                `Another partnership has same slug (id: ${otherWithSameSlug.id})`,
              )
              callback(new Error('Another partnership has same slug'))
            }
            callback()
          },
        },
      ],
      promo_link_url: [
        {
          validator(rule, value, callback) {
            if (!value && form.value.promo_code) {
              callback(new Error('URL is required if promo code is filled in'))
            }
            callback()
          },
        },
      ],
    }

    const saveIndicator = ref(null)
    const showSave = () => {
      saveIndicator.value.show()
    }

    const save = async (emitSave = true) => {
      try {
        await formRef.value.validate()
      } catch (err) {
        if (err !== false) {
          console.error('Got error while trying to validate form:', err)
        }
        root.$message.error('Please correct form errors first')
        return
      }
      const entry = { ...form.value }
      if (!props.value.id) {
        try {
          const response = await store.dispatch('marketplacePartnerships/add', {
            entry,
          })
          Object.assign(entry, response.data)
        } catch (err) {
          root.$message.error('Something went wrong')
          throw err
        }
      } else {
        try {
          await store.dispatch('marketplacePartnerships/update', { entry })
        } catch (err) {
          console.log(' => errors:', err)
          root.$message.error('Something went wrong')
          throw err
        }
      }
      if (emitSave) emit('save', { partnership: entry })
    }
    const debouncedSave = _.debounce(function () {
      save(false).then(() => {
        showSave()
      })
    }, 1200)
    // watch(
    //   () => form.value,
    //   (newVal, oldVal) => {
    //     if (!newVal || !oldVal) return
    //     const areValuesEqual = _.isEqual(newVal, oldVal)
    //     if (props.value.id && areValuesEqual && form.value.name) {
    //       debouncedSave()
    //     }
    //   },
    //   { deep: true },
    // )

    const cancel = () => {
      emit('cancel')
    }

    const saveInProgress = computed(() => {
      const state = store.state.marketplacePartnerships
      return state.addInProgress || state.updateInProgress
    })

    watch(
      'value',
      (newVal) => {
        console.log(' => value changed to:', newVal)
        form.value = { ...initForm(), ...newVal }
      },
      { deep: true },
    )

    const allTags = computed(() => {
      return store.getters['marketplacePartnerships/tags']
    })
    const isTagValid = (tag) => {
      const alreadyExists = new Set(
        allTags.value.map((t) => t.toLowerCase()),
      ).has(tag.toLowerCase())
      return !alreadyExists
    }
    const onTagInvalid = () => {
      root.$message.error('Tag already exists')
    }

    const deleteInProgress = ref(false)
    const confirmRemoval = async () => {
      try {
        const msg = `Are you sure you want to remove partnership <b>${form.value.name}</b>?`
        const buttonText = 'Confirm deletion'
        await root.$confirm(msg, buttonText, {
          dangerouslyUseHTMLString: true,
        })
      } catch (err) {
        // removal cancelled
        return
      }
      try {
        deleteInProgress.value = true
        await store.dispatch('marketplacePartnerships/remove', {
          id: props.value.id,
        })
        cancel()
      } finally {
        deleteInProgress.value = false
      }
    }

    const onImageUploaded = ({ file, url }) => {
      form.value.logo_url = url
    }

    return {
      form,
      formRef,
      formRules,
      save,
      cancel,
      saveInProgress,

      saveIndicator,

      onImageUploaded,

      allTags,
      isTagValid,
      onTagInvalid,

      DEFAULT_PROMO_LINK_URL_TEXT,

      deleteInProgress,
      confirmRemoval,
      AMAZON_ID,
    }
  },
}

function initForm() {
  return {
    id: null,
    name: '',
    slug: '',
    description: '',
    logo_url: '',
    promo_link_url_text: '',
    promo_link_url: '',
    promo_code: '',
    sectors: [],
    is_published: false,
    is_startup: false,
    approved_startups_only: false,
    type: 'PROMO',
    email: '',
    mail_intro_text: '',
  }
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.partnership-form {
  .save-indicator {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    font-size: 20px;
  }

  .image-wrapper {
    padding: 15px 10px;

    .el-image__inner {
      max-width: 300px;
    }
  }

  .el-form-item__content .vb-text-editor {
    line-height: 24px;
  }

  .el-form-item[faded] {
    opacity: 0.25;
  }
}
</style>
